import React, { useState, useEffect, useRef } from 'react';

// ── Design Tokens ─────────────────────────────────────────────────────────
const T = {
  ink:     '#0D0D0F',
  ink2:    '#1A1A20',
  slate:   '#2D2D38',
  muted:   '#6B6B7A',
  line:    '#E8E8F0',
  surface: '#F5F5FA',
  white:   '#FFFFFF',
  lime:    '#C8F23C',
  teal:    '#00BFA5',
};

const BIKES = [
  { id:1, category:'Scooters',       image:'/activa.jpg',    price:'Starting ₹100/hr', desc:'Easy city commute' },
  { id:2, category:'Standard Bikes', image:'/splendor.jpeg', price:'Starting ₹100/hr', desc:'Reliable daily ride' },
  { id:3, category:'Sport Bikes',    image:'/bike1.jpg',     price:'Starting ₹150/hr', desc:'For thrill seekers' },
  { id:4, category:'Electric',       image:'/ether.jpeg',    price:'Starting ₹120/hr', desc:'Eco-friendly ride' },
];

// ── Arrow Button ───────────────────────────────────────────────────────────
function ArrowBtn({ dir, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label={dir === 'prev' ? 'Previous' : 'Next'}
      style={{
        position: 'absolute',
        top: '50%', transform: 'translateY(-50%)',
        [dir === 'prev' ? 'left' : 'right']: 0,
        zIndex: 20,
        width: 48, height: 48, borderRadius: '50%', border: 'none',
        background: hov ? T.lime : T.ink,
        color: hov ? T.ink : T.lime,
        fontSize: 20, fontWeight: 900, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .2s, color .2s',
        boxShadow: '0 8px 24px rgba(0,0,0,.18)',
      }}
    >
      {dir === 'prev' ? '←' : '→'}
    </button>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function BikeSlidingSection() {
  const [cur, setCur]     = useState(0);
  const [anim, setAnim]   = useState(false);
  const [dir, setDir]     = useState(1); // 1 = forward, -1 = backward
  const timerRef          = useRef(null);
  const n = BIKES.length;

  const go = (direction) => {
    if (anim) return;
    setDir(direction);
    setAnim(true);
    setTimeout(() => {
      setCur(p => (p + direction + n) % n);
      setAnim(false);
    }, 320);
  };

  const goTo = (idx) => {
    if (idx === cur || anim) return;
    setDir(idx > cur ? 1 : -1);
    setAnim(true);
    setTimeout(() => { setCur(idx); setAnim(false); }, 320);
  };

  // auto-slide
  useEffect(() => {
    timerRef.current = setInterval(() => go(1), 4500);
    return () => clearInterval(timerRef.current);
  }, [cur, anim]);

  const prev = BIKES[(cur - 1 + n) % n];
  const curr = BIKES[cur];
  const next = BIKES[(cur + 1) % n];

  return (
    <div style={{
      width: '100%', padding: '72px 24px',
      background: T.surface,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700;800;900&family=Space+Grotesk:wght@700&display=swap');
        * { box-sizing: border-box; }
        @keyframes slideInRight { from { opacity:0; transform:translateX(60px) scale(.9); } to { opacity:1; transform:translateX(0) scale(1); } }
        @keyframes slideInLeft  { from { opacity:0; transform:translateX(-60px) scale(.9); } to { opacity:1; transform:translateX(0) scale(1); } }
      `}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span style={{
            display: 'inline-block', marginBottom: 14,
            background: T.ink, color: T.lime,
            fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
            padding: '5px 16px', borderRadius: 40,
          }}>
            Bikes
          </span>
          <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 'clamp(26px, 4vw, 42px)',
            fontWeight: 700, color: T.ink, marginBottom: 8,
          }}>
            Exclusive Deals in Pune
          </h2>
          <p style={{ fontSize: 15, color: T.muted }}>Every ride category, covered</p>
        </div>

        {/* Carousel */}
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <ArrowBtn dir="prev" onClick={() => go(-1)} />

          {/* Desktop — 3-up view */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            padding: '0 64px',
          }}>
            {/* Prev — dim */}
            <div style={{
              flex: '0 0 25%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', textAlign: 'center',
              opacity: 0.3, transform: 'scale(.72)', transition: 'all .35s',
              pointerEvents: 'none', userSelect: 'none',
            }}>
              <div style={{ width: 200, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={prev.image} alt={prev.category}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={e => { e.target.style.display='none'; e.target.parentElement.innerHTML='<span style="font-size:60px;">🏍️</span>'; }}
                />
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.slate, marginTop: 12 }}>{prev.category}</p>
            </div>

            {/* Current — hero */}
            <div style={{
              flex: '0 0 50%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', textAlign: 'center', zIndex: 10,
              animation: anim ? (dir > 0 ? 'slideInRight .32s ease' : 'slideInLeft .32s ease') : 'none',
            }}>
              {/* Card */}
              <div style={{
                background: T.white,
                border: `2px solid ${T.lime}`,
                borderRadius: 24, overflow: 'hidden',
                width: '100%', maxWidth: 400,
                boxShadow: '0 20px 60px rgba(0,0,0,.1)',
              }}>
                <div style={{
                  background: T.ink,
                  height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                }}>
                  <img src={curr.image} alt={curr.category}
                    style={{ width: '90%', height: '90%', objectFit: 'contain' }}
                    onError={e => { e.target.style.display='none'; e.target.parentElement.innerHTML='<span style="font-size:80px;">🏍️</span>'; }}
                  />
                  {/* Live badge */}
                  <div style={{
                    position: 'absolute', top: 14, left: 14,
                    background: T.lime, color: T.ink,
                    fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 20,
                    letterSpacing: .8, textTransform: 'uppercase',
                  }}>
                    Available Now
                  </div>
                </div>
                <div style={{ padding: '20px 24px 24px' }}>
                  <h3 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 22, fontWeight: 700, color: T.ink, marginBottom: 6,
                  }}>
                    {curr.category}
                  </h3>
                  <p style={{ fontSize: 13, color: T.muted, marginBottom: 14 }}>{curr.desc}</p>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <span style={{ fontSize: 11, color: T.muted }}>Starting from</span>
                      <div style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>{curr.price}</div>
                    </div>
                    <button style={{
                      padding: '10px 20px', borderRadius: 12, border: 'none',
                      background: T.ink, color: T.lime,
                      fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    }}>
                      Book →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Next — dim */}
            <div style={{
              flex: '0 0 25%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', textAlign: 'center',
              opacity: 0.3, transform: 'scale(.72)', transition: 'all .35s',
              pointerEvents: 'none', userSelect: 'none',
            }}>
              <div style={{ width: 200, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={next.image} alt={next.category}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={e => { e.target.style.display='none'; e.target.parentElement.innerHTML='<span style="font-size:60px;">🏍️</span>'; }}
                />
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.slate, marginTop: 12 }}>{next.category}</p>
            </div>
          </div>

          <ArrowBtn dir="next" onClick={() => go(1)} />
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 36 }}>
          {BIKES.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} style={{
              border: 'none', borderRadius: 20, cursor: 'pointer',
              transition: 'all .3s',
              width: cur === i ? 28 : 10, height: 10,
              background: cur === i ? T.ink : T.line,
              padding: 0,
            }} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
